/**
 * Theme hook — Sprint 2 F5.
 *
 * Dark/light mode with localStorage persistence. Applies `data-theme` on <html>
 * so CSS custom properties swap.
 */
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'v3_theme';

function getInitialTheme() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
    // No explicit choice yet — match the active/default skin's base. All premium
    // themes are light; only 'classic' is dark. Default skin is Aurora (light).
    const skin = localStorage.getItem('v3_skin') || 'aurora';
    return skin === 'classic' ? 'dark' : 'light';
}

function applyTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    // Keep the legacy Cuba template in sync. Classic pages (Sales / Traffic
    // dashboards, reports, etc.) theme off `body.dark-only` / `body.light-only`,
    // NOT the V3 `data-theme` attribute, so without this they stay light when V3
    // dark mode is on. Use classList.toggle (never body.className=) so V3's own
    // body classes (v3-has-sidebar, v3-themed, layout type) survive.
    const dark = theme === 'dark';
    document.body.classList.toggle('dark-only', dark);
    document.body.classList.toggle('light-only', !dark);
}

export function useTheme() {
    const [theme, setTheme] = useState(getInitialTheme);

    useEffect(() => {
        applyTheme(theme);
        localStorage.setItem(STORAGE_KEY, theme);
    }, [theme]);

    const toggle = useCallback(() => setTheme(t => t === 'dark' ? 'light' : 'dark'), []);

    return { theme, setTheme, toggle, isDark: theme === 'dark' };
}
